import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import InputFormField from 'src/registry/components/forms/InputFormField';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { BillingEntityFormSchemaValues } from '../../utils/formSchema';
import { billingMessages } from 'src/features/billing/utils/messages';
import { commonMessages } from 'src/utils/messages';
import { FINANCIAL_DOCUMENT_PRINT_TEMPLATE } from 'src/features/billing/utils/types';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { FINANCIAL_DOC_TEMPLATE_TRANSLATION_MAP } from 'src/features/billing/utils/constants';
import { Input } from 'src/registry/ui/Input';
import { ImageUpload } from 'src/features/billing/components/ImageUpload';

type BillingEntityFormProps = {
    form: UseFormReturn<BillingEntityFormSchemaValues>;
    onSubmit: (values: BillingEntityFormSchemaValues) => void;
    logoTitle?: string;
};

function BillingEntityForm({ onSubmit, form, logoTitle }: BillingEntityFormProps) {
    const { formatMessage } = useIntl();

    const { financial_document_print_template } = form.watch();

    const isLogoRequired =
        financial_document_print_template &&
        financial_document_print_template !== FINANCIAL_DOCUMENT_PRINT_TEMPLATE.STANDARD_NO_LOGO;

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<BillingEntityFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(commonMessages.name)}
            />
            <InputFormField<BillingEntityFormSchemaValues>
                form={form}
                name="code"
                label={formatMessage(billingMessages.code)}
            />
            <InputFormField<BillingEntityFormSchemaValues>
                form={form}
                name="uic"
                label={formatMessage(billingMessages.legalNumber)}
            />
            <InputFormField<BillingEntityFormSchemaValues>
                form={form}
                name="address"
                label={formatMessage(commonMessages.address)}
            />
            <InputFormField<BillingEntityFormSchemaValues>
                form={form}
                name="city"
                label={formatMessage(commonMessages.city)}
            />
            <InputFormField<BillingEntityFormSchemaValues>
                form={form}
                name="country"
                label={formatMessage(commonMessages.country)}
            />
            <InputFormField<BillingEntityFormSchemaValues>
                form={form}
                name="email"
                type="email"
                required={false}
                label={formatMessage(commonMessages.email)}
            />
            <InputFormField<BillingEntityFormSchemaValues>
                form={form}
                name="phone"
                type="tel"
                required={false}
                label={formatMessage(commonMessages.phone)}
            />
            <InputFormField<BillingEntityFormSchemaValues>
                form={form}
                name="tax_registration_number"
                label={formatMessage(billingMessages.taxNumber)}
            />
            <InputFormField<BillingEntityFormSchemaValues>
                form={form}
                name="representative"
                label={formatMessage(billingMessages.legalRepresentative)}
            />
            <InputFormField<BillingEntityFormSchemaValues>
                form={form}
                name="invoice_prefix"
                required={false}
                label={formatMessage(billingMessages.invoicePrefix)}
            />
            <InputFormField<BillingEntityFormSchemaValues>
                form={form}
                name="invoice_footer"
                required={false}
                label={formatMessage(billingMessages.invoiceFooter)}
            />
            <SelectFormField<BillingEntityFormSchemaValues>
                form={form}
                name="financial_document_print_template"
                required
                label={formatMessage(billingMessages.financialDocPrintTemplate)}
                options={Object.values(FINANCIAL_DOCUMENT_PRINT_TEMPLATE)}
                optionTranslationMap={FINANCIAL_DOC_TEMPLATE_TRANSLATION_MAP}
            />
            <FormField
                control={form.control}
                name="financial_document_print_logo"
                render={({ field }) => {
                    const photoValue = form.watch('financial_document_print_logo');
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required={isLogoRequired}>
                                {formatMessage(billingMessages.financialDocLogo)}
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <ImageUpload
                                        onChange={field.onChange}
                                        imagePreview={
                                            typeof photoValue === 'string'
                                                ? photoValue
                                                : photoValue instanceof File
                                                  ? URL.createObjectURL(photoValue)
                                                  : logoTitle
                                        }
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />

            <FormField
                control={form.control}
                name="financial_document_print_color"
                render={({ field }) => {
                    const color = field.value || '#000000';

                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">
                                <FormattedMessage {...billingMessages.financialDocColor} />
                            </FormLabel>

                            <FormControlStack>
                                <FormControl>
                                    <div className="flex grid-cols-2 items-center gap-3">
                                        <Input
                                            type="color"
                                            value={color}
                                            onChange={(e) => field.onChange(e.target.value)}
                                            className="h-10 cursor-pointer rounded border p-1"
                                        />
                                        <Input
                                            type="text"
                                            value={color}
                                            onChange={(e) => field.onChange(e.target.value)}
                                            className="rounded border px-2 py-1 font-mono"
                                            placeholder="#000000"
                                        />
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
        </Form>
    );
}

export default BillingEntityForm;
